import { IntlShape } from 'react-intl'

import { extendMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { extendMetadataAttestations } from '@lyrahealth-inc/shared-app-logic/src/features/attestations/utils'
import { thisFieldIsRequiredErrorMessage } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { ConnectedServiceAttestation } from '@lyrahealth-inc/shared-app-logic/src/features/connectedService/types'
import { getLegalNoticeLink } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getLegalNoticeLink'
import { Attestations } from '@lyrahealth-inc/shared-app-logic/src/models/attestations/Attestations'
import { FormMetadata } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'

export enum LegalAgreementsField {
  CONSENT_TO_TERMS_OF_USE = 'hasConsentedToTermsOfUse',
  CONSENT_TO_TERMS_OF_USE_AND_HIPAA = 'hasConsentedToTermsOfUseAndHIPAA',
  CONSENT_TO_CANCELLATION_POLICY = 'hasConsentedToCancellationPolicy',
}

const DIVIDER = 'divider'

export type LegalAgreementsMetadataProps = {
  intl: IntlShape
  // True for US registrants and US territory registrants (Puerto Rico, Virgin Islands, Guam, Northern Mariana
  // Islands, American Samoa) — they see the combined Terms of Use + HIPAA checkbox. Everyone else sees the
  // Terms of Use only checkbox.
  requiresHipaaConsent: boolean
  activeLanguage: string
  attestationContents?: Attestations | null
  hideDivider?: boolean
  textColor?: string
  hideLegalAgreements?: boolean
  attestationContentBaseInputStyle?: React.CSSProperties
  connectedServiceAttestation?: ConnectedServiceAttestation
  isProviderLed?: boolean
  shouldShowCancellationPolicy?: boolean
  showEmailCapturePageExperimentV2?: boolean
}

export const generateLegalAgreementsMetadata = ({
  intl,
  requiresHipaaConsent = false,
  activeLanguage,
  attestationContents = null,
  hideDivider,
  textColor,
  hideLegalAgreements = false,
  attestationContentBaseInputStyle,
  connectedServiceAttestation,
  isProviderLed,
  shouldShowCancellationPolicy,
  showEmailCapturePageExperimentV2,
}: LegalAgreementsMetadataProps): FormMetadata => {
  const metadata: FormMetadata = {
    schema: {
      type: 'object',
      properties: {
        [DIVIDER]: {
          name: DIVIDER,
          type: 'string',
          show: !hideDivider,
        },
        [LegalAgreementsField.CONSENT_TO_TERMS_OF_USE]: {
          name: LegalAgreementsField.CONSENT_TO_TERMS_OF_USE,
          title: intl.formatMessage(
            {
              defaultMessage:
                'I agree to Lyra’s <termsOfUseLink>Terms of Use</termsOfUseLink> and <privacyPolicyLink>Privacy Policy</privacyPolicyLink>.',
              description: 'Message to acquire content from user',
            },
            {
              termsOfUseLink: (chunks) => `[${chunks}](${getLegalNoticeLink(activeLanguage, 'TERMS_OF_USE')})`,
              privacyPolicyLink: (chunks) => `[${chunks}](${getLegalNoticeLink(activeLanguage, 'PRIVACY_POLICY')})`,
            },
          ),
          type: 'boolean',
          isTitleFocusable: !!IS_WEB,
          widgetProps: {
            labelSize: BodyTextSize.SMALL,
            titleColor: textColor,
            checkboxAccessibilityLabel: intl.formatMessage({
              defaultMessage: 'Consent checkbox',
              description: 'Accessibility label for the checkbox input element',
            }),
          },
          validation: {
            isBooleanTrue: true,
          },
          show: !requiresHipaaConsent && !hideLegalAgreements,
        },
        [LegalAgreementsField.CONSENT_TO_TERMS_OF_USE_AND_HIPAA]: {
          name: LegalAgreementsField.CONSENT_TO_TERMS_OF_USE_AND_HIPAA,
          title: intl.formatMessage(
            {
              defaultMessage:
                'I agree to Lyra’s <termsOfUseLink>Terms of Use</termsOfUseLink>, <privacyPolicyLink>Privacy Policy</privacyPolicyLink>, <hipaaLink>HIPAA Notice</hipaaLink>, and <emailConsentLink>Consent to Use Personal Email</emailConsentLink>.',
              description: 'Message to acquire content from user',
            },
            {
              termsOfUseLink: (chunks) => `[${chunks}](${getLegalNoticeLink(activeLanguage, 'TERMS_OF_USE')})`,
              hipaaLink: (chunks) => `[${chunks}](${getLegalNoticeLink(activeLanguage, 'HIPAA_NOTICE')})`,
              emailConsentLink: (chunks) => `[${chunks}](${getLegalNoticeLink(activeLanguage, 'EMAIL_CONSENT')})`,
              privacyPolicyLink: (chunks) => `[${chunks}](${getLegalNoticeLink(activeLanguage, 'PRIVACY_POLICY')})`,
            },
          ),
          type: 'boolean',
          isTitleFocusable: !!IS_WEB,
          widgetProps: {
            labelSize: BodyTextSize.SMALL,
            titleColor: textColor,
            checkboxAccessibilityLabel: intl.formatMessage({
              defaultMessage: 'Consent checkbox',
              description: 'Accessibility label for the checkbox input element',
            }),
          },
          validation: {
            isBooleanTrue: true,
          },
          show: requiresHipaaConsent && !hideLegalAgreements,
        },
        [LegalAgreementsField.CONSENT_TO_CANCELLATION_POLICY]: {
          name: LegalAgreementsField.CONSENT_TO_CANCELLATION_POLICY,
          title: intl.formatMessage({
            defaultMessage:
              'I agree to the cancellation policy terms. If you cancel in less than 24 hours or miss a session, your credit/debit card may be charged a late cancellation/no-show payment.',
            description: 'Cancellation policy consent checkbox label for provider-led registration',
          }),
          type: 'boolean',
          isTitleFocusable: !!IS_WEB,
          widgetProps: {
            labelSize: BodyTextSize.SMALL,
            titleColor: textColor,
            checkboxAccessibilityLabel: intl.formatMessage({
              defaultMessage: 'Cancellation policy consent checkbox',
              description: 'Accessibility label for the cancellation policy checkbox',
            }),
          },
          validation: {
            isBooleanTrue: true,
          },
          show: !!isProviderLed && !!shouldShowCancellationPolicy,
        },
      },
    },
    uiSchema: {
      'ui:order': [
        DIVIDER,
        LegalAgreementsField.CONSENT_TO_TERMS_OF_USE,
        LegalAgreementsField.CONSENT_TO_TERMS_OF_USE_AND_HIPAA,
      ],
      [DIVIDER]: { 'ui:field': 'Divider' },
      [LegalAgreementsField.CONSENT_TO_TERMS_OF_USE]: {
        'ui:widget': 'checkbox',
        'ui:options': {
          baseInputStyle: {
            marginBottom: 8,
          },
        },
      },
      [LegalAgreementsField.CONSENT_TO_TERMS_OF_USE_AND_HIPAA]: {
        'ui:widget': 'checkbox',
        'ui:options': {
          baseInputStyle: {
            marginBottom: 8,
          },
        },
      },
      [LegalAgreementsField.CONSENT_TO_CANCELLATION_POLICY]: {
        'ui:widget': 'checkbox',
        'ui:options': {
          baseInputStyle: {
            marginBottom: 8,
          },
        },
      },
    },
  }

  let extendedMetadata = extendMetadataAttestations({
    attestationContents,
    connectedServiceAttestation,
    errorMessage: thisFieldIsRequiredErrorMessage,
    metadata,
    intl,
    checkboxProps: {
      labelSize: BodyTextSize.SMALL,
      titleColor: textColor,
      // We are adding the margin bottom to account for endlines and the margin we should have after one
      markdownStyle: {
        p2: { marginBottom: 8 },
      },
    },
    baseInputStyle: {
      ...attestationContentBaseInputStyle,
      ...(!!showEmailCapturePageExperimentV2 && { baseInputStyle: { marginBottom: 0 } }),
    },
  })

  if (isProviderLed) {
    extendedMetadata = extendMetadata(extendedMetadata, {
      schema: { properties: {} },
      uiSchema: {
        'ui:order': [LegalAgreementsField.CONSENT_TO_CANCELLATION_POLICY],
      },
    })
  }

  return extendedMetadata
}
